import { FontIcons, OnlyForScreenReader } from '@brandfolder/react';
import { AttachmentDto } from '@brandfolder-panel/sdk';

import { forwardRef, useContext, useEffect } from 'react';

import { toast } from 'react-hot-toast';

import styled from 'styled-components';

import { BlockDrag } from '@components/blocks';
import { AsideContext } from '@context/aside';
import { BlockContext } from '@context/blocks';
import { DragAndDropContext } from '@context/drag-and-drop';
import { LocaleContext } from '@context/locale';
import { PageContext } from '@context/page';
import { PublishContext } from '@context/publish';
import { BlockTypes } from '@enums/blocks';
import { Attachment } from '@typings/attachment';
import { getAttachment, getIsAttachmentUrlSmartCdn } from '@utilities/attachment';
import { createBlock, createBlockImageData, insertBlock } from '@utilities/block';

import { AsideButtonWrapper } from './aside-button-wrapper';
import { AsideCloseButton } from './aside-close-button';
import { AsideDialog, OmittedAsideDialogProps } from './aside-dialog';
import { AsidePopout, HoverHelpPositions } from './aside-popout';
import { ColorsPopout } from './colors/colors-popout';
import { PanelUI } from './panel-ui';

interface AsideBlocksProps extends OmittedAsideDialogProps {}

const asideBlocksId = 'aside-blocks';
const UnstyledAsideBlocks = forwardRef<HTMLDivElement, AsideBlocksProps>((props, ref) => {
  const {
    setShowBlocks,
    setShowCodeBlock,
    setShowImage,
    setShowText,
    showCodeBlock,
    showImage,
    showText
  } = useContext(AsideContext);
  const { blocks, setBlocks } = useContext(BlockContext);
  const { dragged } = useContext(DragAndDropContext);
  const { locale } = useContext(LocaleContext);
  const { page } = useContext(PageContext);
  const { setPublish } = useContext(PublishContext);

  if (!page) return null;

  const setAttachment = (dto: AttachmentDto, attachment?: Attachment) => {
    const block = createBlock({
      data: createBlockImageData(dto, attachment),
      locale,
      pageKey: page.key,
      position: 0,
      type: BlockTypes.Image
    });

    setBlocks([...insertBlock(block, blocks, page.key)]);
    setPublish(true);
    setShowBlocks(false);
  };

  const handleAttachment = async (dto: AttachmentDto): Promise<void> => {
    if (getIsAttachmentUrlSmartCdn(dto.url)) {
      setAttachment(dto);
    } else {
      const apiDatum = await getAttachment(dto.id);
      if (apiDatum) {
        setAttachment(dto, apiDatum.data.attributes);
      } else {
        toast.error('Error saving image. Please try again.');
      }
    }
  };

  const openOnHover = dragged ? false : true;

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const handleEscClose = (e: any): void => {
    if (e.key === 'Escape') {
      setShowBlocks(false);
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    window.addEventListener('keyup', handleEscClose);
  }, []);

  return (
    <AsideDialog {...props} id={asideBlocksId} ref={ref} title="">
      <>
        <AsideButtonWrapper level={2}>
          <AsidePopout
            content={
              <PanelUI
                onSelect={(attachmentDto) => {
                  handleAttachment(attachmentDto);
                }}
              />
            }
            hoverHelpPosition={HoverHelpPositions.Bottom}
            icon={FontIcons.Image}
            id={`${asideBlocksId}-image`}
            label="Open add an image block"
            open={showImage}
            openOnHover={openOnHover}
            setOpen={setShowImage}
            title="Images"
            tooltip="Images"
          />
        </AsideButtonWrapper>
        <AsideButtonWrapper level={2}>
          <AsidePopout
            content={<BlockDrag type={BlockTypes.Text}>Text</BlockDrag>}
            icon={FontIcons.FontType}
            height={178}
            hoverHelpPosition={HoverHelpPositions.Both}
            id={`${asideBlocksId}-text`}
            label="Open add a text block"
            open={showText}
            openOnHover={openOnHover}
            setOpen={setShowText}
            title="Text"
            tooltip="Text"
          />
        </AsideButtonWrapper>
        <AsideButtonWrapper level={2}>
          <AsidePopout
            content={<BlockDrag type={BlockTypes.Html}>HTML</BlockDrag>}
            height={178}
            hoverHelpPosition={HoverHelpPositions.Top}
            icon={FontIcons.Embed}
            id={`${asideBlocksId}-code`}
            label="Open add a code block"
            open={showCodeBlock}
            openOnHover={openOnHover}
            setOpen={setShowCodeBlock}
            title="Custom Code"
            tooltip="Code"
          />
        </AsideButtonWrapper>
        <AsideButtonWrapper level={2}>
          <ColorsPopout />
        </AsideButtonWrapper>
        <OnlyForScreenReader>
          <AsideButtonWrapper level={2}>
            <AsideCloseButton label="Close adding blocks" setOpen={setShowBlocks} tooltip="Close" />
          </AsideButtonWrapper>
        </OnlyForScreenReader>
      </>
    </AsideDialog>
  );
});

const StyledAsideBlocks = styled(UnstyledAsideBlocks)`
  #${asideBlocksId}-image-popout {
    .bf-popout__focus-lock {
      height: 100%;
      display: flex;
      flex-direction: column;

      .bf-popout__body {
        padding: 0;
        flex: 1 1 100%;

        .bf-popout__autofocus {
          height: 100%;
        }
      }
    }
  }
`;

export const AsideBlocks = forwardRef<HTMLDivElement, AsideBlocksProps>((props, ref) => {
  return <StyledAsideBlocks {...props} ref={ref} />;
});
